import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { TemplateRow } from '../components/table/row/template-row.types';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import ApiService from '../../../../services/api-service.util';

type GetTemplatesResponse = {
  count: number;
  records: TemplateRow[];
  totalPages: number;
};

const PAGE_SIZE = 50;

export function useInfiniteTemplates({
  companyId,
  enabled = true,
  equityPlanId,
}: {
  companyId: string | undefined;
  enabled?: boolean;
  equityPlanId: string | undefined;
}) {
  const { data, fetchNextPage, hasNextPage, isPending } = useInfiniteQuery<GetTemplatesResponse>({
    queryKey: [QueryKey.GET_TEMPLATES, 'infinite', companyId, equityPlanId],
    queryFn: ({ pageParam = 0 }) =>
      ApiService.get<GetTemplatesResponse>(
        { endpoint: BackendRoute.TEMPLATES },
        {
          queryParams: {
            companyId,
            equityPlanId,
            skip: pageParam,
            take: PAGE_SIZE,
          },
        },
      ),
    enabled: Boolean(companyId && equityPlanId) && enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * PAGE_SIZE : undefined,
  });

  const templates = useMemo(
    () => data?.pages?.reduce<TemplateRow[]>((acc, page) => [...acc, ...page.records], []) ?? [],
    [data?.pages],
  );

  return {
    templates,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isPending,
  };
}
